import { z } from "zod";

/**
 * The display currency: how one environment shows the platform's internal unit.
 *
 * Every stored and wire amount is an integer count of US cents. The currency is
 * presentation only — a name, a symbol, a multiplier and an icon that decide how a
 * number of cents reads on a storefront, in the dashboard and inside the opener. No
 * core function reads it, and no stored number changes when a tenant changes it.
 *
 * `formatAmount` and `parseAmount` are the only two conversions. `@loot-os/client`
 * and `@loot-os/react` carry hand-written mirrors of this module (they are published
 * and cannot depend on this private package); their contract tests fail on drift.
 */

/** Internal cents per displayed whole unit. 100 shows 1,250 cents as 12.50; 1 shows 1,250. */
export const CURRENCY_MULTIPLIERS = [1, 10, 100, 1000] as const;
export type CurrencyMultiplier = (typeof CURRENCY_MULTIPLIERS)[number];
export const currencyMultiplierSchema = z
  .literal([...CURRENCY_MULTIPLIERS])
  .describe("Internal cents per displayed whole unit: 1, 10, 100 or 1000.");

export const currencySchema = z.object({
  /** Singular unit name, e.g. "credit". */
  name: z.string().min(1).max(50),
  /** Plural unit name, resolved server-side (`${name}s` when the tenant stored none). */
  namePlural: z.string().min(1).max(50),
  /** Prefix written with no space before the amount ("$12.50"); "" for none ("1,250 gems"). */
  symbol: z.string().max(3),
  multiplier: currencyMultiplierSchema,
  /**
   * Tenant-uploaded currency icon as SVG source, "" when unset. Embed via `svgIconDataUri`
   * in an `<img>` — never inline. See `./svgIcon.ts`.
   */
  iconSvg: z.string(),
});
export type Currency = z.infer<typeof currencySchema>;

/** Credits: the default for every environment. 1,250 cents reads "$12.50". */
export const DEFAULT_CURRENCY: Currency = {
  name: "credit",
  namePlural: "credits",
  symbol: "$",
  multiplier: 100,
  iconSvg: "",
};

/** Real money (Stripe charges, disputes, revenue) and platform pages with no tenant in scope. */
export const USD: Currency = {
  name: "dollar",
  namePlural: "dollars",
  symbol: "$",
  multiplier: 100,
  iconSvg: "",
};

/** The stored multiplier as a member of the closed set; throws naming the value on drift. */
export function parseCurrencyMultiplier(value: number): CurrencyMultiplier {
  const found = CURRENCY_MULTIPLIERS.find((m) => m === value);
  if (found === undefined) {
    throw new RangeError(`currency multiplier must be one of 1, 10, 100, 1000; got ${value}`);
  }
  return found;
}

/** Decimal places a displayed amount carries: 0 for 1, 1 for 10, 2 for 100, 3 for 1000. */
export function currencyFractionDigits(multiplier: CurrencyMultiplier): 0 | 1 | 2 | 3 {
  switch (multiplier) {
    case 1:
      return 0;
    case 10:
      return 1;
    case 100:
      return 2;
    case 1000:
      return 3;
  }
}

/**
 * The locale is pinned rather than left to the browser: the dashboard renders a preview of
 * the storefront, and a thousands separator that depended on the staff member's locale would
 * disagree with the store it claims to show.
 */
const NUMBER_FORMATS = [0, 1, 2, 3].map(
  (digits) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }),
);

export interface FormatAmountOptions {
  /**
   * Labels under artwork (wheel items, result cards) shorten a displayed value of 1,000 or
   * more to one decimal and a "k" ("$1.3k"), the boxed `formatPrice` rule. Anything the
   * shopper pays or is told a total for stays exact.
   */
  compact?: boolean;
}

/** Cents → the amount as a shopper reads it: sign, symbol, digits, no spaces, no unit name. */
export function formatAmount(
  cents: number,
  currency: Pick<Currency, "symbol" | "multiplier">,
  opts: FormatAmountOptions = {},
): string {
  if (!Number.isFinite(cents)) {
    throw new RangeError(`formatAmount: amount is not a finite number of cents: ${cents}`);
  }
  const digits = currencyFractionDigits(currency.multiplier);
  const value = Math.abs(cents) / currency.multiplier;
  const sign = cents < 0 ? "-" : "";
  const body =
    opts.compact && value >= 1000
      ? `${(value / 1000).toFixed(1)}k`
      : (NUMBER_FORMATS[digits] as Intl.NumberFormat).format(value);
  return `${sign}${currency.symbol}${body}`;
}

/** The unit word for an amount: singular for exactly one displayed unit, plural otherwise. */
export function currencyUnitName(
  cents: number,
  currency: Pick<Currency, "name" | "namePlural" | "multiplier">,
): string {
  return cents === currency.multiplier ? currency.name : currency.namePlural;
}

/**
 * An amount with its unit where the currency has no symbol ("1,250 gems"), the bare symbol
 * form where it has one ("$12.50" — two denominations on one number would read as two
 * currencies).
 */
export function formatAmountWithUnit(cents: number, currency: Currency): string {
  const amount = formatAmount(cents, currency);
  return currency.symbol === "" ? `${amount} ${currencyUnitName(cents, currency)}` : amount;
}

/**
 * A typed amount ("12.50", "$1,250.00", "-5") → integer cents, or null when the text is not
 * an amount in this currency: no digits, more decimals than the multiplier allows, or a value
 * outside the safe integer range. Integer arithmetic throughout, so no float rounding.
 */
export function parseAmount(
  input: string,
  currency: Pick<Currency, "symbol" | "multiplier">,
): number | null {
  let text = input.trim();
  let negative = false;
  if (text.startsWith("-")) {
    negative = true;
    text = text.slice(1).trimStart();
  }
  if (currency.symbol !== "" && text.startsWith(currency.symbol)) {
    text = text.slice(currency.symbol.length).trimStart();
  }
  text = text.replace(/,/g, "");
  const match = /^(\d*)(?:\.(\d+))?$/.exec(text);
  if (!match) return null;
  const whole = match[1] ?? "";
  const fraction = match[2] ?? "";
  if (whole === "" && fraction === "") return null;
  const digits = currencyFractionDigits(currency.multiplier);
  if (fraction.length > digits) return null;
  const wholeCents = Number(whole === "" ? "0" : whole) * currency.multiplier;
  const fractionCents = digits === 0 ? 0 : Number(fraction.padEnd(digits, "0"));
  const cents = wholeCents + fractionCents;
  if (!Number.isSafeInteger(cents)) return null;
  return negative ? -cents : cents;
}

/** The five `storefront_configs` currency columns, named as Drizzle infers them. */
export interface StoredCurrencyColumns {
  currencyName: string;
  currencyNamePlural: string | null;
  currencySymbol: string;
  currencyMultiplier: number;
  currencyIconSvg: string;
}

/**
 * Stored columns → the wire currency. The one place the plural falls back to `${name}s`
 * (a stored "" counts as absent), and the one place the stored multiplier is checked against
 * the closed set — a silent fallback there would mis-display every amount.
 */
export function resolveCurrency(row: StoredCurrencyColumns): Currency {
  return {
    name: row.currencyName,
    namePlural: row.currencyNamePlural || `${row.currencyName}s`,
    symbol: row.currencySymbol,
    multiplier: parseCurrencyMultiplier(row.currencyMultiplier),
    iconSvg: row.currencyIconSvg,
  };
}
